import {
  type PullRequest,
  type PullResponse,
  type PushRequest,
  type PushResponse,
  type SyncCursor,
  type SyncEntity,
  type SyncOp,
  SYNC_ENTITIES,
} from "@coach/shared";
import type { LocalDb, SqlValue } from "./local-db.js";
import { ack, bumpAttempts, peek, pendingCount } from "./outbox.js";

/**
 * Bidirectional delta-sync engine.
 *
 *   ┌──────────────┐  pull   ┌────────┐  apply   ┌──────────────┐
 *   │ server (Hono)│ ──────► │ engine │ ───────► │ local SQLite │
 *   │              │ ◄────── │        │ ◄─────── │              │
 *   └──────────────┘  push   └────────┘  peek    └──────────────┘
 *
 * Lifecycle:
 *   - `start()` runs an immediate `syncNow()` then schedules polling.
 *   - `stop()` cancels the poll loop.
 *   - `syncNow()` is reentrancy-safe via an internal lock.
 *
 * Backoff: exponential on transport failure (network/5xx), reset on success.
 *
 * Conflicts: the server is authoritative. When `push` returns `stale`, the
 * client drops its outbox entry — the truth will arrive on the next `pull`.
 */

export type SyncStatus =
  | { kind: "idle"; lastSyncedAt: number | null; pending: number }
  | { kind: "syncing"; startedAt: number }
  | { kind: "offline"; nextAttemptAt: number; error: string; pending: number }
  | { kind: "error"; error: string; pending: number };

export interface SyncTransport {
  pull(req: PullRequest): Promise<PullResponse>;
  push(req: PushRequest): Promise<PushResponse>;
}

export interface SyncEngineOptions {
  db: LocalDb;
  transport: SyncTransport;
  /** Stable per-install UUID — used as `clientId` in push/pull. */
  clientId: string;
  /** Default poll interval when online. */
  pollIntervalMs?: number;
  /** Cap on backoff between failures. */
  maxBackoffMs?: number;
  onStatus?: (status: SyncStatus) => void;
  /**
   * Fired after a pull has applied at least one change. Lets the UI invalidate
   * its query caches so views refresh without polling.
   */
  onApplied?: (entities: ReadonlySet<SyncEntity>) => void;
  /**
   * Fired after a push returned conflicts (once they are acked/bumped).
   * Lets the UI tell the user why an op was rejected — e.g. a new team
   * dropped with `message: "team_limit_reached"` — instead of the row
   * silently vanishing on the next pull.
   */
  onConflicts?: (conflicts: PushResponse["conflicts"]) => void;
}

export interface SyncEngine {
  start(): void;
  stop(): void;
  /** Trigger an immediate push+pull. Safe to call from UI on user action. */
  syncNow(): Promise<void>;
  /** Current snapshot of status (also fires via `onStatus`). */
  status(): SyncStatus;
}

const DEFAULT_POLL_MS = 30_000;
const DEFAULT_MAX_BACKOFF_MS = 5 * 60_000;
const INITIAL_BACKOFF_MS = 2_000;

export function createSyncEngine(opts: SyncEngineOptions): SyncEngine {
  const pollMs = opts.pollIntervalMs ?? DEFAULT_POLL_MS;
  const maxBackoff = opts.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight: Promise<void> | null = null;
  let lastSyncedAt: number | null = null;
  let backoffMs = 0;
  let stopped = false;
  let status: SyncStatus = { kind: "idle", lastSyncedAt: null, pending: 0 };

  const setStatus = (s: SyncStatus) => {
    status = s;
    opts.onStatus?.(s);
  };

  async function loadCursor(): Promise<SyncCursor> {
    const rows = await opts.db.query<{ entity: string; rev: number }>(
      `SELECT entity, rev FROM sync_cursor`,
    );
    const cursor: Record<string, number> = {};
    for (const e of SYNC_ENTITIES) cursor[e] = 0;
    for (const r of rows) cursor[r.entity] = r.rev;
    return cursor as SyncCursor;
  }

  async function writeCursor(db: LocalDb, cursor: SyncCursor): Promise<void> {
    for (const [entity, rev] of Object.entries(cursor)) {
      await db.exec(
        `INSERT INTO sync_cursor(entity, rev) VALUES(?, ?)
           ON CONFLICT(entity) DO UPDATE SET rev = excluded.rev WHERE excluded.rev > sync_cursor.rev`,
        [entity, rev],
      );
    }
  }

  async function applyChanges(
    db: LocalDb,
    changes: PullResponse["changes"],
  ): Promise<void> {
    await db.transaction(async (tx) => {
      for (const ch of changes) {
        const table = TABLE_FOR_ENTITY[ch.entity];
        const cols = COLUMNS_FOR_ENTITY[ch.entity];
        const row = ch.row as Record<string, unknown>;
        const values: SqlValue[] = cols.map((c) =>
          normalizeForSqlite(row[c.field]),
        );
        const placeholders = cols.map(() => "?").join(", ");
        const updateAssignments = cols
          .filter((c) => c.field !== "id")
          .map((c) => `${c.column} = excluded.${c.column}`)
          .join(", ");
        await tx.exec(
          `INSERT INTO ${table} (${cols.map((c) => c.column).join(", ")})
             VALUES (${placeholders})
             ON CONFLICT(id) DO UPDATE SET ${updateAssignments}
             WHERE excluded.rev > ${table}.rev OR ${table}.rev IS NULL`,
          values,
        );
      }
    });
  }

  async function doPush(): Promise<{ allAccepted: boolean }> {
    const ops: SyncOp[] = await peek(opts.db, 500);
    if (ops.length === 0) return { allAccepted: true };
    const resp = await opts.transport.push({ clientId: opts.clientId, ops });
    const acceptedIds = resp.accepted.map((a) => a.opId);
    // Treat "stale" conflicts as ack too — the truth will come on next pull.
    // `forbidden` (caller no longer owns the row) and `validation` (the op
    // itself is malformed — FK violation, schema mismatch, …) are dropped
    // as well: retrying would loop forever and a `validation` reply tells
    // us the payload is fundamentally unrunnable, not transiently broken.
    const dropIds = [
      ...acceptedIds,
      ...resp.conflicts
        .filter(
          (c) =>
            c.reason === "stale" ||
            c.reason === "forbidden" ||
            c.reason === "validation",
        )
        .map((c) => c.opId),
    ];
    const retryIds = resp.conflicts
      .filter(
        (c) =>
          c.reason !== "stale" &&
          c.reason !== "forbidden" &&
          c.reason !== "validation",
      )
      .map((c) => c.opId);
    await opts.db.transaction(async (tx) => {
      await ack(tx, dropIds);
      await bumpAttempts(tx, retryIds);
    });
    if (resp.conflicts.length > 0) {
      // A UI callback error must not surface as a sync failure.
      try {
        opts.onConflicts?.(resp.conflicts);
      } catch (err) {
        console.warn("[sync] onConflicts handler failed", err);
      }
    }
    return { allAccepted: retryIds.length === 0 };
  }

  async function doPull(): Promise<{ done: boolean }> {
    let hasMore = true;
    let done = true;
    let iterations = 0;
    const touched = new Set<SyncEntity>();
    while (hasMore && iterations < 20 && !stopped) {
      const cursor = await loadCursor();
      const resp = await opts.transport.pull({
        clientId: opts.clientId,
        cursor,
        limit: 500,
      });
      if (resp.changes.length > 0) {
        await applyChanges(opts.db, resp.changes);
        for (const ch of resp.changes) touched.add(ch.entity);
      }
      await opts.db.transaction((tx) => writeCursor(tx, resp.nextCursor));
      hasMore = resp.hasMore;
      iterations += 1;
    }
    if (hasMore) done = false;
    if (touched.size > 0) opts.onApplied?.(touched);
    return { done };
  }

  async function runOnce(): Promise<void> {
    setStatus({ kind: "syncing", startedAt: Date.now() });
    try {
      await doPush();
      await doPull();
      lastSyncedAt = Date.now();
      backoffMs = 0;
      const pending = await pendingCount(opts.db);
      setStatus({ kind: "idle", lastSyncedAt, pending });
    } catch (err) {
      const message = (err as Error).message || String(err);
      backoffMs = Math.min(
        backoffMs === 0 ? INITIAL_BACKOFF_MS : backoffMs * 2,
        maxBackoff,
      );
      const pending = await pendingCount(opts.db).catch(() => 0);
      setStatus({
        kind: "offline",
        nextAttemptAt: Date.now() + backoffMs,
        error: message,
        pending,
      });
    }
  }

  function schedule(): void {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    const delay = backoffMs > 0 ? backoffMs : pollMs;
    timer = setTimeout(() => {
      void syncNow();
    }, delay);
  }

  async function syncNow(): Promise<void> {
    if (inflight) return inflight;
    inflight = (async () => {
      try {
        await runOnce();
      } finally {
        inflight = null;
        schedule();
      }
    })();
    return inflight;
  }

  return {
    start() {
      stopped = false;
      void syncNow();
    },
    stop() {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
    syncNow,
    status: () => status,
  };
}

// ─── Entity → SQLite column mapping ────────────────────────────────────────
// Domain rows arrive from the server as the Prisma shape (camelCase) but the
// local SQLite schema uses snake_case. This map lets us round-trip them.

interface ColumnMap {
  /** Field name on the JSON object returned by the server. */
  field: string;
  /** Column name in SQLite. */
  column: string;
}

const META_COLUMNS: ColumnMap[] = [
  { field: "id", column: "id" },
  { field: "orgId", column: "org_id" },
  { field: "rev", column: "rev" },
  { field: "updatedAt", column: "updated_at" },
  { field: "deletedAt", column: "deleted_at" },
  { field: "clientId", column: "client_id" },
];

const TABLE_FOR_ENTITY: Record<SyncEntity, string> = {
  team: "team",
  team_coach: "team_coach",
  player: "player",
  calendar_event: "calendar_event",
  formation: "formation",
  board: "board",
  media_ref: "media_ref",
};

const COLUMNS_FOR_ENTITY: Record<SyncEntity, ColumnMap[]> = {
  team: [
    ...META_COLUMNS,
    { field: "name", column: "name" },
    { field: "league", column: "league" },
  ],
  team_coach: [
    ...META_COLUMNS,
    { field: "teamId", column: "team_id" },
    { field: "userId", column: "user_id" },
    { field: "role", column: "role" },
  ],
  player: [
    ...META_COLUMNS,
    { field: "ownerKind", column: "owner_kind" },
    { field: "ownerId", column: "owner_id" },
    { field: "name", column: "name" },
    { field: "imageRef", column: "image_ref" },
    { field: "position", column: "position" },
    { field: "foot", column: "foot" },
    { field: "shirtNumber", column: "shirt_number" },
    { field: "email", column: "email" },
    { field: "notes", column: "notes" },
    { field: "statsJson", column: "stats_json" },
  ],
  calendar_event: [
    ...META_COLUMNS,
    { field: "ownerKind", column: "owner_kind" },
    { field: "ownerId", column: "owner_id" },
    { field: "title", column: "title" },
    { field: "description", column: "description" },
    { field: "type", column: "type" },
    { field: "startsAt", column: "starts_at" },
    { field: "endsAt", column: "ends_at" },
    { field: "location", column: "location" },
    { field: "seriesId", column: "series_id" },
    { field: "allDay", column: "all_day" },
  ],
  formation: [
    ...META_COLUMNS,
    { field: "ownerKind", column: "owner_kind" },
    { field: "ownerId", column: "owner_id" },
    { field: "name", column: "name" },
    { field: "preset", column: "preset" },
    { field: "positionsJson", column: "positions_json" },
  ],
  board: [
    ...META_COLUMNS,
    { field: "ownerKind", column: "owner_kind" },
    { field: "ownerId", column: "owner_id" },
    { field: "kind", column: "kind" },
    { field: "name", column: "name" },
    { field: "snapshotJson", column: "snapshot_json" },
  ],
  media_ref: [
    ...META_COLUMNS,
    { field: "ownerKind", column: "owner_kind" },
    { field: "ownerId", column: "owner_id" },
    { field: "s3Key", column: "s3_key" },
    { field: "mime", column: "mime" },
    { field: "bytes", column: "bytes" },
    { field: "width", column: "width" },
    { field: "height", column: "height" },
  ],
};

function normalizeForSqlite(value: unknown): SqlValue {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "object") return JSON.stringify(value);
  return value as SqlValue;
}
